import React, { useEffect, useState } from "react";
import { listTeams } from "../lib/api";
import { Loader2, Users, Crown, Lock, Unlock, Calendar } from "lucide-react";

function MemberChip({ user }) {
  if (!user) return null;
  const label = user.global_name || user.username || "Unknown";
  return (
    <div
      className="flex items-center gap-2 bg-[#1E1233]/70 rounded-md pl-1 pr-3 py-1 border border-[#9D4CDD]/20"
      data-testid={`member-chip-${user.id}`}
      title={label}
    >
      {user.avatar_url ? (
        <img
          src={user.avatar_url}
          alt={label}
          className="h-6 w-6 rounded-full"
          loading="lazy"
        />
      ) : (
        <div className="h-6 w-6 rounded-full bg-[#9D4CDD]/40" />
      )}
      <span className="text-xs text-white/85 truncate max-w-[140px]">
        {label}
      </span>
    </div>
  );
}

function TeamCard({ team }) {
  const color = team.role_color || "#9D4CDD";
  const created = team.created_at
    ? new Date(team.created_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div
      className="glass-card rounded-xl p-6 border-l-4 transition-all hover:-translate-y-1 hover:border-[#FF5E00]"
      style={{ borderLeftColor: color }}
      data-testid={`team-card-${team.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0 flex items-center gap-3">
          {team.role_icon ? (
            <img
              src={team.role_icon}
              alt=""
              className="h-9 w-9 rounded-md shrink-0"
              data-testid={`team-role-icon-${team.id}`}
            />
          ) : team.role_emoji ? (
            <span
              className="text-2xl leading-none shrink-0"
              data-testid={`team-role-emoji-${team.id}`}
            >
              {team.role_emoji}
            </span>
          ) : null}
          <div className="min-w-0">
            <h3
              className="font-display uppercase font-black text-2xl tracking-wider truncate"
              style={{ color }}
            >
              {team.name}
            </h3>
            {created && (
              <div className="flex items-center gap-1 text-xs text-white/45 mt-1">
                <Calendar className="w-3 h-3" /> Founded {created}
              </div>
            )}
          </div>
        </div>
        {team.locked ? (
          <span
            className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-display font-bold px-2 py-1 rounded bg-[#FF5E00]/15 text-[#FF5E00] border border-[#FF5E00]/40"
            data-testid={`team-locked-${team.id}`}
          >
            <Lock className="w-3 h-3" /> Locked
          </span>
        ) : (
          <span
            className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-display px-2 py-1 rounded bg-white/5 text-white/55 border border-white/10"
            data-testid={`team-open-${team.id}`}
          >
            <Unlock className="w-3 h-3" /> Open
          </span>
        )}
      </div>

      {team.captain && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF5E00] font-display font-bold mb-2 flex items-center gap-1">
            <Crown className="w-3 h-3" /> Captain
          </div>
          <div
            className="flex items-center gap-3 bg-[#1E1233]/70 rounded-lg p-3 border border-[#FF5E00]/30"
            data-testid={`team-captain-${team.id}`}
          >
            {team.captain.avatar_url ? (
              <img
                src={team.captain.avatar_url}
                alt=""
                className="h-10 w-10 rounded-full ring-2 ring-[#FF5E00]/40"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-[#9D4CDD]/40 flex items-center justify-center">
                <Crown className="w-5 h-5 text-[#FF5E00]" />
              </div>
            )}
            <span className="font-display font-bold text-white truncate">
              {team.captain.global_name || team.captain.username}
            </span>
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-display font-bold mb-2">
          Members · {team.members?.length || 0}
        </div>
        {team.members && team.members.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {team.members.map((m) => (
              <MemberChip key={m.id} user={m} />
            ))}
          </div>
        ) : (
          <div className="text-xs text-white/40 italic">
            No additional members yet
          </div>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-[#9D4CDD]/15 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-display text-white/45">
        <span>Seed pts</span>
        <span className="font-bold text-white/80">{team.seeding_points ?? 0}</span>
      </div>
    </div>
  );
}

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listTeams()
      .then(setTeams)
      .finally(() => setLoading(false));
  }, []);

  // Show bot-synced teams first (those with a captain), then any others
  const sorted = [...teams].sort((a, b) => {
    const aBot = a.captain_id ? 1 : 0;
    const bBot = b.captain_id ? 1 : 0;
    return bBot - aBot || (b.seeding_points || 0) - (a.seeding_points || 0);
  });

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      data-testid="teams-page"
    >
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5E00]/40 bg-[#FF5E00]/5 text-[#FF5E00] text-xs uppercase tracking-[0.25em] font-display mb-4">
          <Users className="w-3 h-3" /> Roster
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
          Competing <span className="text-gradient-purple">Teams</span>
        </h1>
        <p className="mt-3 text-white/65 max-w-2xl">
          Rosters synced live from the Discord tournament bot.
        </p>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5E00]" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center text-white/60">
          No teams synced yet. The Discord bot will populate this page once
          it pushes data to <code className="text-[#FF5E00]">/api/teams/sync-from-bot</code>.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sorted.map((t) => (
            <TeamCard key={t.id} team={t} />
          ))}
        </div>
      )}
    </div>
  );
}
